import React from 'react';
import { TrendingUp, ArrowUpRight } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { macroDetailData } from '../../../data/macroData';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

interface GDPPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const GDPPanel: React.FC<GDPPanelProps> = ({ isMinimized, onMinimize }) => {
  const { gdp } = macroDetailData;
  const maxShare = Math.max(...gdp.composition.map(c => c.share));

  return (
    <Panel title="GDP OVERVIEW" subtitle="2025" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Section - Giant GDP Number */}
        <div className="text-center py-6 px-4 bg-gradient-to-b from-data-green/10 to-transparent rounded-lg border border-data-green/20 mb-4">
          <div className="text-xs text-data-green uppercase tracking-widest font-semibold mb-2">
            Nominal GDP
          </div>
          <div className="text-5xl md:text-6xl font-mono font-black text-white mb-2">
            <span className="text-3xl md:text-4xl align-top">$</span>
            {gdp.current}
            <span className="text-3xl md:text-4xl">B</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <ArrowUpRight size={16} className="text-data-green" />
            <span className="text-lg font-semibold text-data-green">+{gdp.growth}% YoY</span>
          </div>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-xs text-gray-500 uppercase mb-1">Growth Rate</div>
            <div className="text-3xl font-mono font-bold text-data-green">{gdp.growth}%</div>
            <div className="text-xs text-gray-500">9M 2025</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-xs text-gray-500 uppercase mb-1">Per Capita</div>
            <div className="text-3xl font-mono font-bold text-white">${(gdp.perCapita / 1000).toFixed(1)}K</div>
            <div className="text-xs text-gray-500">${(gdp.perCapitaPPP / 1000).toFixed(1)}K PPP</div>
          </div>
        </div>

        {/* GDP Composition */}
        <div className="bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30 mb-4">
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">GDP Composition</div>
          <div className="space-y-3">
            {gdp.composition.map((item) => (
              <div key={item.sector}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-300">{item.sector}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-semibold text-white">{item.share}%</span>
                    <span className={`text-xs font-mono ${item.growth >= 6 ? 'text-data-green' : 'text-data-amber'}`}>
                      +{item.growth}%
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-terminal-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-data-amber rounded-full transition-all"
                    style={{ width: `${(item.share / maxShare) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* GDP History Chart */}
        <div className="flex-1 bg-terminal-bg/40 rounded-lg p-4 border border-terminal-border/30 min-h-[150px]">
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">GDP History (USD B)</div>
          <ResponsiveContainer width="100%" height="85%">
            <AreaChart data={gdp.history} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <defs>
                <linearGradient id="gdpGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00d26a" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00d26a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="year"
                tick={{ fontSize: 10, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                width={35}
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #00d26a',
                  borderRadius: '8px',
                  fontSize: '12px',
                  padding: '10px 14px',
                }}
                labelStyle={{ color: '#ffffff', fontWeight: 'bold' }}
                formatter={(value: number) => [`$${value}B`, 'GDP']}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#00d26a"
                strokeWidth={2}
                fill="url(#gdpGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.IMF} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default GDPPanel;
